import { useEffect, useRef, useState } from 'react';
import { OctagonX } from 'lucide-react';
import { useI18n } from '../../i18n/index.js';
import type { ApiError } from '../../lib/api.js';
import { stopSignal } from '../../lib/stopSignal.js';
import { stationErrorText } from '../../lib/stationError.js';

// ── מסך עצירה (אלי 21/10/2026) ─────────────────────────────────────────────
// „חלק שעוצר בייצור צריך התראה גדולה יותר בסריקה" — כדי שלא ייראה כמו „חסר שרטוט".
// מסך מלא אדום, הסיבה במילים של מי שעצר, ולמי לפנות. צפצוף קצר — בעמדה מסתכלים על
// החלק ולא על המסך. נסגר רק בלחיצה מפורשת „הבנתי — לא מתחיל" (או Enter/Esc אחרי שנייה,
// כדי שסריקה שנייה מהאקדח לא תסגור אותו בלי שנקרא).

const WHO_KEY: Record<string, string> = {
  NC_BLOCKING: 'stop.who.nc',
  PRODUCTION_HOLD: 'stop.who.hold',
  ECO_OPEN: 'stop.who.eco',
  FAI_PENDING: 'stop.who.fai',
  ENG_INSPECTION_PENDING: 'stop.who.inspection',
  DRAWING_MISSING: 'stop.who.drawingWait',
};

function beep(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = 'square'; o.frequency.value = 520; g.gain.value = 0.08;
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.frequency.setValueAtTime(380, ctx.currentTime + 0.18);
    o.stop(ctx.currentTime + 0.42);
    o.onended = () => { void ctx.close(); };
  } catch { /* בלי צליל — המסך מספיק */ }
}

export function StopScreen(): JSX.Element | null {
  const { t } = useI18n();
  const [err, setErr] = useState<ApiError | null>(null);
  const [armed, setArmed] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);

  useEffect(() => stopSignal.on((e) => { setErr(e); setArmed(false); beep(); }), []);
  useEffect(() => {
    if (!err) return undefined;
    const tm = window.setTimeout(() => { setArmed(true); btn.current?.focus(); }, 1000);
    return () => window.clearTimeout(tm);
  }, [err]);
  useEffect(() => {
    if (!err || !armed) return undefined;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); setErr(null); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [err, armed]);

  if (!err) return null;
  const d = (err.details ?? {}) as { partNumber?: string };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-rose-700 p-6 text-white" dir="auto"
      role="alertdialog" aria-modal="true" aria-labelledby="stop-title" aria-describedby="stop-reason" data-testid="stop-screen">
      <div className="flex w-full max-w-3xl flex-col items-center gap-5 text-center">
        <OctagonX className="h-28 w-28" strokeWidth={1.6} aria-hidden="true" />
        <h1 id="stop-title" className="text-5xl font-black leading-tight sm:text-6xl">{t('stop.title')}</h1>
        {d.partNumber && <div className="font-mono text-3xl font-black" dir="ltr">{d.partNumber}</div>}
        <p id="stop-reason" className="max-w-2xl rounded-2xl bg-white/15 px-6 py-4 text-2xl font-bold leading-snug">
          {stationErrorText(err, t)}
        </p>
        <p className="text-xl font-semibold text-rose-50">{t(WHO_KEY[err.code] ?? 'stop.who.hold')}</p>
        <button ref={btn} type="button" disabled={!armed} onClick={() => setErr(null)}
          className="mt-2 rounded-2xl bg-white px-10 py-4 text-2xl font-black text-rose-800 shadow-lg focus:outline-none focus-visible:ring-8 focus-visible:ring-white/50 disabled:opacity-60">
          {t('stop.ok')}
        </button>
      </div>
    </div>
  );
}
